import React, { useState, useEffect, useCallback } from 'react';
import { Reorder } from 'framer-motion';
import { Row, Col, Typography, Input, Button, Popconfirm, Checkbox } from 'antd';
import { DragOutlined, SaveOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import './DraggableBehaviors.css';
import OrderingAPI from '../../utils/OrderingAPI';

const { Text } = Typography;
const { TextArea } = Input;

const DraggableBehaviors = ({ 
    behaviors = [], 
    isEditing, 
    onBehaviorChange, 
    onBehaviorAdd,
    onBehaviorDelete,
    onBehaviorToggleReversed,
    onOrderChange,
    onSaveOrder,
    attributeId,
    attributeType = 'leader' // 'leader' | 'culture' | 'selfcare'
}) => {
    const [items, setItems] = useState([]);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        // Convertir behaviors a formato de items con IDs únicos y estables
        const behaviorItems = behaviors.map((behavior, index) => {
            // Si behavior es un objeto (con cab_id o lab_id), extraer los datos
            if (typeof behavior === 'object' && behavior !== null) {
                const behaviorId = behavior.cab_id || behavior.lab_id || behavior.sbe_id;
                const behaviorText = behavior.cab_behavior || behavior.lab_behavior || behavior.sbe_behavior || '';
                return {
                    id: `behavior_${attributeId}_${index}`,
                    value: behaviorText,
                    originalIndex: index,
                    behaviorId: behaviorId, // ID real de la base de datos
                    order: index,
                    reversed: Boolean(behavior.sbe_reversed)
                };
            } else {
                // Si behavior es solo texto (retrocompatibilidad)
                return {
                    id: `behavior_${attributeId}_${index}`,
                    value: behavior || '',
                    originalIndex: index,
                    behaviorId: null,
                    order: index,
                    reversed: false
                };
            }
        });
        
        setItems(behaviorItems);
    }, [behaviors, attributeId]);

    const handleReorder = async (newItems) => {
        const reorderedItems = newItems.map((item, index) => ({
            ...item,
            order: index
        }));
        
        setItems(reorderedItems);
        
        // Guardar automáticamente el nuevo orden
        try {
            const orderData = reorderedItems
                .filter(item => item.behaviorId !== null) // Solo items con ID de DB
                .map(item => {
                    if (attributeType === 'culture') {
                        return { cab_id: item.behaviorId, cab_order: item.order };
                    }
                    if (attributeType === 'selfcare') {
                        return { sbe_id: item.behaviorId, sbe_order: item.order };
                    }
                    return { lab_id: item.behaviorId, lab_order: item.order };
                });
            
            if (onOrderChange) {
                onOrderChange(orderData);
            }
            
            // Guardar automáticamente
            if (onSaveOrder) {
                await onSaveOrder(orderData);
            } else {
                // Usar la API directamente si no hay función onSaveOrder
                if (attributeType === 'culture') {
                    await OrderingAPI.updateCultureBehaviorsOrder(orderData);
                } else if (attributeType === 'selfcare') {
                    await OrderingAPI.updateSelfcareBehaviorsOrder(orderData);
                } else {
                    await OrderingAPI.updateLeaderBehaviorsOrder(orderData);
                }
            }
            
        } catch (error) {
            console.error('Error al guardar orden automáticamente:', error);
            setHasChanges(true); // Si hay error, mostrar botón para guardar manual
        }
    };

    const handleBehaviorChange = useCallback((itemId, newValue) => {
        const updatedItems = items.map(item => 
            item.id === itemId ? { ...item, value: newValue } : item
        );
        setItems(updatedItems);
        
        // Actualizar en el componente padre
        const itemIndex = items.findIndex(item => item.id === itemId);
        if (itemIndex !== -1 && onBehaviorChange) {
            onBehaviorChange(items[itemIndex].originalIndex, newValue);
        }
    }, [items, onBehaviorChange]);

    const handleBehaviorReversedChange = useCallback((itemId, checked) => {
        const updatedItems = items.map(item =>
            item.id === itemId ? { ...item, reversed: checked } : item
        );
        setItems(updatedItems);

        const itemIndex = items.findIndex(item => item.id === itemId);
        if (itemIndex !== -1 && onBehaviorToggleReversed) {
            onBehaviorToggleReversed(items[itemIndex].originalIndex, checked);
        }
    }, [items, onBehaviorToggleReversed]);

    const handleAddBehavior = () => {
        const newIndex = items.length;
        const newBehavior = {
            id: `behavior_${attributeId}_${newIndex}`,
            value: '',
            originalIndex: newIndex,
            order: newIndex,
            reversed: false
        };
        
        const updatedItems = [...items, newBehavior];
        setItems(updatedItems);
        
        // Actualizar en el componente padre primero
        if (onBehaviorAdd) {
            onBehaviorAdd('');
        }
    };

    const handleDeleteBehavior = async (itemId) => {
        const itemIndex = items.findIndex(item => item.id === itemId);
        if (itemIndex === -1) return;

        const itemToDelete = items[itemIndex];
        
        // Si el comportamiento tiene un ID de base de datos, eliminarlo del servidor
        if (itemToDelete.behaviorId) {
            try {
                if (attributeType === 'culture') {
                    await OrderingAPI.deleteCultureBehavior(itemToDelete.behaviorId);
                } else if (attributeType === 'selfcare') {
                    await OrderingAPI.deleteSelfcareBehavior(itemToDelete.behaviorId);
                } else {
                    await OrderingAPI.deleteLeaderBehavior(itemToDelete.behaviorId);
                }
            } catch (error) {
                console.error('Error al eliminar del servidor:', error);
                return; // No continuar si falla la eliminación del servidor
            }
        }
        
        // Usar el originalIndex que ya tenemos guardado en el item
        const originalIndex = itemToDelete.originalIndex;
        
        const updatedItems = items
            .filter(item => item.id !== itemId)
            .map((item, index) => ({ ...item, order: index }));
        
        setItems(updatedItems);
        setHasChanges(true);
        
        // Pasar el índice original del item en la lista de props
        if (onBehaviorDelete && originalIndex !== undefined) {
            onBehaviorDelete(originalIndex);
        }
    };

    const saveOrder = async () => {
        try {
            const orderData = items
                .filter(item => item.behaviorId !== null) // Solo items con ID de DB
                .map(item => {
                    if (attributeType === 'culture') {
                        return { cab_id: item.behaviorId, cab_order: item.order };
                    }
                    if (attributeType === 'selfcare') {
                        return { sbe_id: item.behaviorId, sbe_order: item.order };
                    }
                    return { lab_id: item.behaviorId, lab_order: item.order };
                });
            
            if (onSaveOrder) {
                await onSaveOrder(orderData);
            } else {
                // Usar la API directamente si no hay función onSaveOrder
                if (attributeType === 'culture') {
                    await OrderingAPI.updateCultureBehaviorsOrder(orderData);
                } else if (attributeType === 'selfcare') {
                    await OrderingAPI.updateSelfcareBehaviorsOrder(orderData);
                } else {
                    await OrderingAPI.updateLeaderBehaviorsOrder(orderData);
                }
            }
            
            setHasChanges(false);
        } catch (error) {
            console.error('Error al guardar orden:', error);
        }
    };

    if (!isEditing) {
        // Modo solo lectura
        return (
            <div>
                {behaviors.map((behavior, index) => {
                    // Extraer texto del behavior (puede ser objeto o string)
                    const behaviorText = typeof behavior === 'object' && behavior !== null
                        ? (behavior.cab_behavior || behavior.lab_behavior || behavior.sbe_behavior || '')
                        : (behavior || '');
                    const behaviorReversed = typeof behavior === 'object' && behavior !== null
                        ? Boolean(behavior.sbe_reversed)
                        : false;
                    
                    return behaviorText && behaviorText.trim() !== '' && (
                        <Row key={index} align="middle" style={{ marginBottom: 10 }}>
                            <Col span={4}>
                                <Text>Comportamiento {index + 1}:</Text>
                            </Col>
                            <Col span={attributeType === 'selfcare' ? 16 : 20}>
                                <Text>{behaviorText}</Text>
                            </Col>
                            {attributeType === 'selfcare' && (
                                <Col span={4}>
                                    <Text strong>Inverso:</Text> <Text>{behaviorReversed ? 'Sí' : 'No'}</Text>
                                </Col>
                            )}
                        </Row>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="draggable-behaviors">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Text strong>Arrastra para reordenar comportamientos:</Text>
                <div>
                    <Button 
                        type="dashed" 
                        size="small" 
                        icon={<PlusOutlined />}
                        onClick={handleAddBehavior}
                        style={{ marginRight: 8 }}
                    >
                        Agregar
                    </Button>
                    {hasChanges && (
                        <Button 
                            type="primary" 
                            size="small" 
                            icon={<SaveOutlined />}
                            onClick={saveOrder}
                        >
                            Guardar Orden
                        </Button>
                    )}
                </div>
            </div>
            
            <Reorder.Group 
                axis="y" 
                values={items} 
                onReorder={handleReorder}
                className="reorder-group"
            >
                {items.map((item, index) => (
                    <Reorder.Item 
                        key={item.id} 
                        value={item}
                        className="reorder-item"
                        whileDrag={{ scale: 1.02, zIndex: 1000 }}
                        dragElastic={0.1}
                    >
                        <Row align="middle" style={{ width: '100%' }}>
                            <Col span={1} className="drag-handle">
                                <DragOutlined style={{ color: '#00b7a3', cursor: 'grab' }} />
                            </Col>
                            <Col span={4} style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                <Text>Comportamiento {index + 1}:</Text>
                            </Col>
                            <Col span={attributeType === 'selfcare' ? 14 : 17}>
                                <TextArea
                                    autoSize={{ minRows: 2, maxRows: 4 }}
                                    value={item.value}
                                    onChange={(e) => handleBehaviorChange(item.id, e.target.value)}
                                    placeholder={`Ingrese el comportamiento ${index + 1}`}
                                />
                            </Col>
                            {attributeType === 'selfcare' && (
                                <Col span={3} style={{ textAlign: 'center' }}>
                                    <Checkbox
                                        checked={Boolean(item.reversed)}
                                        onChange={(e) => handleBehaviorReversedChange(item.id, e.target.checked)}
                                    >
                                        Inverso
                                    </Checkbox>
                                </Col>
                            )}
                            <Col span={2} style={{ textAlign: 'center' }}>
                                <Popconfirm
                                    title="¿Eliminar comportamiento?"
                                    description="Esta acción no se puede deshacer"
                                    onConfirm={() => handleDeleteBehavior(item.id)}
                                    okText="Sí"
                                    cancelText="No"
                                >
                                    <Button 
                                        danger 
                                        size="small" 
                                        icon={<DeleteOutlined />}
                                        style={{ marginTop: 4 }}
                                    />
                                </Popconfirm>
                            </Col>
                        </Row>
                    </Reorder.Item>
                ))}
            </Reorder.Group>
            
            {items.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                    <Text type="secondary">No hay comportamientos. Haz clic en "Agregar" para crear el primero.</Text>
                </div>
            )}
        </div>
    );
};

export default DraggableBehaviors;